'use client';

import {
  createContext,
  use,
  useEffect,
  useEffectEvent,
  useState,
} from 'react';

import {
  animate,
  motion,
  useInView,
  useMotionValue,
  useReducedMotion,
  useTransform,
} from 'framer-motion';
import {
  BatteryFull,
  CalendarDays,
  House,
  Pause,
  Play,
  Signal,
  Ticket,
  UserRound,
  Wifi,
} from 'lucide-react';

import { cn } from '@kit/ui/utils';

import type { MarketingLocale } from '~/lib/marketing/site';

import { PHONE_SIZE } from './demo-sizes';
import './demo-kit.css';

/*
 * Shared kit for the animated product demos on the marketing site.
 *
 * Every demo is a looping, step-based timeline: a fixed list of step
 * durations drives a single `step` index, and each element derives its state
 * from that index. Framer Motion tweens between states, so a demo re-renders
 * once per step rather than once per frame. Timelines only advance while the
 * demo is on screen and not hovered, and prefers-reduced-motion freezes the
 * demo on its most informative step.
 *
 * All data shown inside demos is fictional. Demos never render currency
 * symbols (tenants choose their own currency) and only depict features the
 * product actually ships.
 */

export interface DemoText {
  en: string;
  ar: string;
}

export interface DemoProps {
  locale: MarketingLocale;
  className?: string;
  /** Called each time the demo finishes a full loop. */
  onLoop?: () => void;
}

export function localize<T extends Record<string, DemoText>>(
  copy: T,
  locale: MarketingLocale,
): { [K in keyof T]: string } {
  const out = {} as { [K in keyof T]: string };

  for (const key of Object.keys(copy) as (keyof T)[]) {
    out[key] = copy[key]![locale];
  }

  return out;
}

export const DEMO_COLORS = {
  ink: '#202020',
  graphite: '#2b2b2b',
  sand: '#ede8e2',
  bone: '#f7f6f4',
  smoke: '#dcd7cf',
  line: '#ebe7e1',
  muted: '#8a857c',
  blue: '#a9c8ff',
  sky: '#e6f0ff',
  mint: '#ddf5ec',
  success: '#2f7a63',
  warning: '#9a6412',
  warningBg: '#fbf0dc',
  whatsapp: '#1faa59',
  whatsappBubble: '#d9fdd3',
  whatsappWall: '#efeae2',
} as const;

/** Fictional venue brands used inside the white-label app demos. */
export const DEMO_BRANDS = {
  padel: {
    name: { en: 'Northside Padel', ar: 'نورثسايد بادل' },
    mark: 'N',
    accent: '#1f4e79',
    accentSoft: '#e4edf6',
  },
  studio: {
    name: { en: 'Linen Studio', ar: 'استوديو لينن' },
    mark: 'L',
    accent: '#9a5b43',
    accentSoft: '#f5ebe5',
  },
  club: {
    name: { en: 'The Harbour Club', ar: 'نادي هاربر' },
    mark: 'H',
    accent: '#2f4a3a',
    accentSoft: '#e5ede7',
  },
  gym: {
    name: { en: 'Forge Fitness', ar: 'فورج فتنس' },
    mark: 'F',
    accent: '#c2410c',
    accentSoft: '#fdeee6',
  },
  academy: {
    name: { en: 'Summit Academy', ar: 'أكاديمية سَمِت' },
    mark: 'S',
    accent: '#b4532a',
    accentSoft: '#f8e9e1',
  },
  clinic: {
    name: { en: 'Cedar Physio', ar: 'سيدار للعلاج الطبيعي' },
    mark: 'C',
    accent: '#2f6f73',
    accentSoft: '#e3f0ef',
  },
} as const;

export type DemoBrand = (typeof DEMO_BRANDS)[keyof typeof DEMO_BRANDS];

export const EASE_OUT_EXPO = [0.16, 1, 0.3, 1] as const;
export const SPRING_SNAPPY = { type: 'spring', stiffness: 420, damping: 34 } as const;
export const SPRING_SOFT = { type: 'spring', stiffness: 220, damping: 26 } as const;

/* -------------------------------------------------------------------------- */
/* Timeline                                                                   */
/* -------------------------------------------------------------------------- */

export interface DemoTimeline {
  /** True while the demo is on screen and motion is allowed. */
  isRunning: boolean;
  /** Current step index (0-based). */
  step: number;
  /** Increments every time the timeline wraps; use as a React key to replay. */
  cycle: number;
  /** True when motion is reduced: the demo is frozen on `staticStep`. */
  isStatic: boolean;
  onPointerEnter: () => void;
  onPointerLeave: () => void;
}

/**
 * Lets a visible pause control freeze every demo inside it (WCAG 2.2.2:
 * moving content that lasts more than five seconds needs a way to stop it).
 */
const DemoMotionContext = createContext({ paused: false });
const DemoRunningContext = createContext(false);

export function DemoMotionProvider({
  paused,
  children,
}: React.PropsWithChildren<{ paused: boolean }>) {
  return <DemoMotionContext value={{ paused }}>{children}</DemoMotionContext>;
}

const PAUSE_LABELS = {
  pause: { en: 'Pause animation', ar: 'إيقاف الحركة' },
  play: { en: 'Play animation', ar: 'تشغيل الحركة' },
} as const;

export function DemoPauseButton({
  paused,
  onToggle,
  locale,
  className,
}: {
  paused: boolean;
  onToggle: () => void;
  locale: MarketingLocale;
  className?: string;
}) {
  const label = paused ? PAUSE_LABELS.play[locale] : PAUSE_LABELS.pause[locale];

  return (
    <button
      type="button"
      onClick={onToggle}
      aria-label={label}
      aria-pressed={paused}
      title={label}
      className={cn(
        'flex h-9 w-9 items-center justify-center rounded-full bg-white/90 text-[#202020] shadow-[0_6px_18px_-8px_rgba(0,0,0,0.45)] ring-1 ring-black/5 backdrop-blur transition-colors hover:bg-white',
        className,
      )}
    >
      {paused ? <Play className="h-3.5 w-3.5" /> : <Pause className="h-3.5 w-3.5" />}
    </button>
  );
}

/**
 * Drives a looping step sequence. `durations[i]` is how long step i is held,
 * in milliseconds. Pass a module-level constant so the array identity is
 * stable across renders. `stageRef` is the element whose visibility gates
 * the clock (attach it to the demo's DemoStage); the caller owns the ref so
 * the returned timeline stays a plain render value.
 */
export function useDemoTimeline(
  stageRef: React.RefObject<HTMLElement | null>,
  durations: readonly number[],
  {
    staticStep = durations.length - 1,
    onLoop,
  }: { staticStep?: number; onLoop?: () => void } = {},
): DemoTimeline {
  const inView = useInView(stageRef, { amount: 0.3 });
  const reducedMotion = useReducedMotion() ?? false;
  const { paused } = use(DemoMotionContext);
  const [state, setState] = useState({ step: 0, cycle: 0, hovered: false });
  const notifyLoop = useEffectEvent(() => onLoop?.());

  // Timers are the one legitimate effect here: the timeline is a pure
  // function of wall-clock time while the demo is visible.
  useEffect(() => {
    if (reducedMotion || paused || !inView || state.hovered) {
      return;
    }

    const id = window.setTimeout(() => {
      if (state.step + 1 >= durations.length) {
        notifyLoop();
      }

      setState((prev) => {
        const next = prev.step + 1;

        return next >= durations.length
          ? { ...prev, step: 0, cycle: prev.cycle + 1 }
          : { ...prev, step: next };
      });
    }, durations[state.step]);

    return () => window.clearTimeout(id);
  }, [durations, inView, paused, reducedMotion, state.hovered, state.step]);

  return {
    isRunning: inView && !reducedMotion && !paused && !state.hovered,
    step: reducedMotion ? staticStep : state.step,
    cycle: state.cycle,
    isStatic: reducedMotion,
    onPointerEnter: () => setState((prev) => ({ ...prev, hovered: true })),
    onPointerLeave: () => setState((prev) => ({ ...prev, hovered: false })),
  };
}

/**
 * Root element of every demo. The whole illustration is exposed to assistive
 * tech as a single image with a descriptive label; hovering pauses it.
 */
export function DemoStage({
  ref,
  timeline,
  label,
  className,
  children,
}: React.PropsWithChildren<{
  ref: React.Ref<HTMLDivElement>;
  timeline: DemoTimeline;
  label: string;
  className?: string;
}>) {
  return (
    <div
      ref={ref}
      role="img"
      aria-label={label}
      onPointerEnter={timeline.onPointerEnter}
      onPointerLeave={timeline.onPointerLeave}
      className={cn('relative select-none font-roboto', className)}
    >
      <DemoRunningContext value={timeline.isRunning}>
        {children}
      </DemoRunningContext>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Scaling                                                                    */
/* -------------------------------------------------------------------------- */

/**
 * Renders a fixed-size design and scales it to the container width, so a
 * 760px dashboard mock stays pixel-consistent on a 340px phone screen.
 *
 * The box reserves its final height with aspect-ratio (capped at the design
 * height × maxScale) from the very first server paint. Container units and
 * trigonometry produce a unitless scale without newer CSS typed division.
 */
export function FitScale({
  width,
  height,
  maxScale = 1,
  className,
  children,
}: React.PropsWithChildren<{
  width: number;
  height: number;
  maxScale?: number;
  className?: string;
}>) {
  return (
    <div
      data-test="demo-fit-container"
      className={cn('relative w-full', className)}
      style={{
        aspectRatio: `${width} / ${height}`,
        maxHeight: height * maxScale,
        containerType: 'inline-size',
      }}
    >
      <div
        data-test="demo-fit-canvas"
        className="absolute top-0"
        style={{
          width,
          height,
          left: '50%',
          '--demo-fit-angle': `atan2(100cqw, ${width}px)`,
          transform: `translateX(-50%) scale(min(${maxScale}, tan(var(--demo-fit-angle))))`,
          transformOrigin: '50% 0',
        } as React.CSSProperties}
      >
        {children}
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Device frames                                                              */
/* -------------------------------------------------------------------------- */

export { PHONE_SIZE } from './demo-sizes';

/**
 * iPhone-style frame. Content area is 270 x 580 with a 44px status bar, so
 * app screens should lay out inside `absolute inset-0 pt-[44px]`.
 */
export function PhoneFrame({
  className,
  screenClassName,
  statusTone = 'dark',
  children,
}: React.PropsWithChildren<{
  className?: string;
  screenClassName?: string;
  /** Colour of the status-bar glyphs. */
  statusTone?: 'dark' | 'light';
}>) {
  return (
    <div
      className={cn(
        'relative rounded-[50px] bg-[#141414] p-[10px]',
        'shadow-[0_50px_100px_-30px_rgba(0,0,0,0.55),0_0_0_1.5px_rgba(255,255,255,0.07)_inset]',
        className,
      )}
      style={PHONE_SIZE}
    >
      <div
        className={cn(
          'relative h-full w-full overflow-hidden rounded-[40px] bg-white',
          screenClassName,
        )}
      >
        <div
          className={cn(
            'absolute inset-x-0 top-0 z-[60] flex h-[44px] items-center justify-between px-7 text-[13px] font-semibold',
            statusTone === 'dark' ? 'text-[#111]' : 'text-white',
          )}
          dir="ltr"
        >
          <span>9:41</span>
          <span className="flex items-center gap-1">
            <Signal className="h-3.5 w-3.5" strokeWidth={2.5} />
            <Wifi className="h-3.5 w-3.5" strokeWidth={2.5} />
            <BatteryFull className="h-4 w-4" strokeWidth={2} />
          </span>
        </div>
        <div className="absolute left-1/2 top-[10px] z-[61] h-[26px] w-[88px] -translate-x-1/2 rounded-full bg-black" />
        {children}
        <div
          className={cn(
            'absolute bottom-[7px] left-1/2 z-[61] h-[4px] w-[100px] -translate-x-1/2 rounded-full',
            statusTone === 'dark' ? 'bg-black/80' : 'bg-white/80',
          )}
        />
      </div>
    </div>
  );
}

/** Desktop browser window chrome around an admin screen. */
export function BrowserFrame({
  url,
  className,
  children,
}: React.PropsWithChildren<{ url: string; className?: string }>) {
  return (
    <div
      className={cn(
        'overflow-hidden rounded-[14px] border border-[#dcd7cf] bg-white',
        'shadow-[0_40px_80px_-30px_rgba(32,32,32,0.35)]',
        className,
      )}
    >
      <div
        className="flex h-9 items-center gap-3 border-b border-[#ebe7e1] bg-[#f7f6f4] px-3.5"
        dir="ltr"
      >
        <span className="flex gap-1.5">
          <span className="h-2.5 w-2.5 rounded-full bg-[#dcd7cf]" />
          <span className="h-2.5 w-2.5 rounded-full bg-[#dcd7cf]" />
          <span className="h-2.5 w-2.5 rounded-full bg-[#dcd7cf]" />
        </span>
        <span className="mx-auto flex h-5 min-w-[220px] items-center justify-center rounded-md bg-white px-3 font-roboto-mono text-[10px] text-[#8a857c]">
          {url}
        </span>
        <span className="w-[42px]" />
      </div>
      {children}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Small building blocks                                                      */
/* -------------------------------------------------------------------------- */

/** A card that floats above other content (so it carries a shadow). */
export function FloatingCard({
  className,
  children,
}: React.PropsWithChildren<{ className?: string }>) {
  return (
    <div
      className={cn(
        'rounded-[16px] border border-[#ebe7e1] bg-white',
        'shadow-[0_18px_40px_-18px_rgba(32,32,32,0.35)]',
        className,
      )}
    >
      {children}
    </div>
  );
}

const AVATAR_TONES = [
  'bg-[#e6f0ff] text-[#2c4c80]',
  'bg-[#ddf5ec] text-[#2f7a63]',
  'bg-[#ede8e2] text-[#5c554b]',
  'bg-[#f5ebe5] text-[#8a4a33]',
  'bg-[#e5ede7] text-[#2f4a3a]',
] as const;

export function Avatar({
  initials,
  tone = 0,
  size = 28,
  className,
}: {
  initials: string;
  tone?: number;
  size?: number;
  className?: string;
}) {
  return (
    <span
      className={cn(
        'inline-flex shrink-0 items-center justify-center rounded-full font-medium ring-2 ring-white',
        AVATAR_TONES[tone % AVATAR_TONES.length],
        className,
      )}
      style={{ width: size, height: size, fontSize: Math.round(size * 0.38) }}
    >
      {initials}
    </span>
  );
}

/** Check mark that draws itself when `show` flips to true. */
export function DrawCheck({
  show,
  size = 20,
  color = 'currentColor',
  strokeWidth = 2.6,
  delay = 0,
}: {
  show: boolean;
  size?: number;
  color?: string;
  strokeWidth?: number;
  delay?: number;
}) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <motion.path
        d="M5 12.5l4.2 4.2L19 7"
        stroke={color}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
        initial={false}
        animate={{ pathLength: show ? 1 : 0, opacity: show ? 1 : 0 }}
        transition={{ duration: 0.45, ease: EASE_OUT_EXPO, delay }}
      />
    </svg>
  );
}

/** Solid round badge with a self-drawing check. */
export function CheckBadge({
  show,
  size = 22,
  bg = DEMO_COLORS.success,
  delay = 0,
}: {
  show: boolean;
  size?: number;
  bg?: string;
  delay?: number;
}) {
  return (
    <motion.span
      className="inline-flex shrink-0 items-center justify-center rounded-full text-white"
      style={{ width: size, height: size, background: bg }}
      initial={false}
      animate={{ scale: show ? 1 : 0.4, opacity: show ? 1 : 0 }}
      transition={{ ...SPRING_SNAPPY, delay }}
    >
      <DrawCheck show={show} size={size * 0.7} delay={delay + 0.08} />
    </motion.span>
  );
}

export function TypingDots({ color = DEMO_COLORS.muted }: { color?: string }) {
  const isRunning = use(DemoRunningContext);

  return (
    <span className="inline-flex items-center gap-1" aria-hidden="true">
      {[0, 1, 2].map((index) => (
        <motion.span
          key={index}
          className="block h-1.5 w-1.5 rounded-full"
          style={{ background: color }}
          animate={isRunning ? { y: [0, -3, 0], opacity: [0.4, 1, 0.4] } : { y: 0, opacity: 0.7 }}
          transition={
            isRunning ? { duration: 0.9, repeat: Infinity, delay: index * 0.15 } : { duration: 0 }
          }
        />
      ))}
    </span>
  );
}

/** Number that tweens to `value`; renders the final value when motion is reduced. */
export function AnimatedNumber({
  value,
  format = (n) => Math.round(n).toLocaleString('en-US'),
  duration = 1.1,
}: {
  value: number;
  format?: (n: number) => string;
  duration?: number;
}) {
  const reducedMotion = useReducedMotion() ?? false;
  const motionValue = useMotionValue(value);
  const display = useTransform(motionValue, format);

  useEffect(() => {
    if (reducedMotion) {
      motionValue.set(value);
      return;
    }

    const controls = animate(motionValue, value, {
      duration,
      ease: EASE_OUT_EXPO,
    });

    return () => controls.stop();
  }, [duration, motionValue, reducedMotion, value]);

  return <motion.span className="tabular-nums">{display}</motion.span>;
}

/** Expanding ring that marks a simulated tap. Remounts per `id` to replay. */
export function TapRipple({
  show,
  className,
  color = 'rgba(32,32,32,0.18)',
}: {
  show: boolean;
  className?: string;
  color?: string;
}) {
  if (!show) {
    return null;
  }

  return (
    <motion.span
      aria-hidden="true"
      className={cn('pointer-events-none absolute rounded-full', className)}
      style={{ background: color, width: 36, height: 36, marginLeft: -18, marginTop: -18 }}
      initial={{ scale: 0.2, opacity: 0.9 }}
      animate={{ scale: 2.4, opacity: 0 }}
      transition={{ duration: 0.7, ease: 'easeOut' }}
    />
  );
}

/** Deterministic QR-like glyph (decorative only, encodes nothing). */
export function FakeQr({
  size = 96,
  color = DEMO_COLORS.ink,
  seed = 7,
}: {
  size?: number;
  color?: string;
  seed?: number;
}) {
  const cells = 25;
  const unit = size / cells;
  const modules: [number, number][] = [];
  let state = seed * 9301 + 49297;

  const isFinder = (x: number, y: number) =>
    (x < 8 && y < 8) || (x > cells - 9 && y < 8) || (x < 8 && y > cells - 9);

  for (let y = 0; y < cells; y++) {
    for (let x = 0; x < cells; x++) {
      state = (state * 9301 + 49297) % 233280;

      if (!isFinder(x, y) && state / 233280 > 0.52) {
        modules.push([x, y]);
      }
    }
  }

  const finder = (x: number, y: number) => (
    <g key={`${x}-${y}`}>
      <rect x={x * unit} y={y * unit} width={7 * unit} height={7 * unit} rx={unit * 1.4} fill={color} />
      <rect x={(x + 1) * unit} y={(y + 1) * unit} width={5 * unit} height={5 * unit} rx={unit} fill="#fff" />
      <rect x={(x + 2) * unit} y={(y + 2) * unit} width={3 * unit} height={3 * unit} rx={unit * 0.6} fill={color} />
    </g>
  );

  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} aria-hidden="true">
      {modules.map(([x, y]) => (
        <rect
          key={`${x}:${y}`}
          x={x * unit + unit * 0.08}
          y={y * unit + unit * 0.08}
          width={unit * 0.84}
          height={unit * 0.84}
          rx={unit * 0.25}
          fill={color}
        />
      ))}
      {finder(0, 0)}
      {finder(cells - 7, 0)}
      {finder(0, cells - 7)}
    </svg>
  );
}

/** Square brand mark used in the fictional venue apps. */
export function BrandMark({
  brand,
  size = 30,
}: {
  brand: DemoBrand;
  size?: number;
}) {
  return (
    <span
      className="inline-flex shrink-0 items-center justify-center rounded-[9px] font-bold text-white"
      style={{
        width: size,
        height: size,
        background: brand.accent,
        fontSize: Math.round(size * 0.48),
      }}
    >
      {brand.mark}
    </span>
  );
}

/**
 * iOS-style switch. The knob moves with a layout animation (not an x offset)
 * so it slides the right way in both LTR and RTL.
 */
export function DemoToggle({
  on,
  accent,
  delay = 0,
}: {
  on: boolean;
  accent: string;
  delay?: number;
}) {
  return (
    <motion.span
      className={cn(
        'inline-flex h-[22px] w-[38px] shrink-0 items-center rounded-full p-[2px]',
        on ? 'justify-end' : 'justify-start',
      )}
      initial={false}
      animate={{ backgroundColor: on ? accent : DEMO_COLORS.smoke }}
      transition={{ duration: 0.25, delay: on ? delay : 0 }}
    >
      <motion.span
        layout
        className="block h-[18px] w-[18px] rounded-full bg-white shadow-sm"
        transition={{ ...SPRING_SNAPPY, delay: on ? delay : 0 }}
      />
    </motion.span>
  );
}

const TAB_ICONS = [House, CalendarDays, Ticket, UserRound] as const;

/** Bottom tab bar of the member app. Labels: Home, Book, Bookings, Profile. */
export function AppTabBar({
  active,
  accent,
  labels,
}: {
  active: number;
  accent: string;
  labels: readonly [string, string, string, string];
}) {
  return (
    <div className="absolute inset-x-0 bottom-0 z-[5] flex h-[64px] items-start justify-around border-t border-[#ebe7e1] bg-white/95 pt-2.5">
      {TAB_ICONS.map((Icon, index) => (
        <span
          key={labels[index]}
          className="flex w-14 flex-col items-center gap-0.5 text-[9px]"
          style={{ color: index === active ? accent : '#a8a298' }}
        >
          <Icon className="h-[18px] w-[18px]" strokeWidth={index === active ? 2.3 : 1.8} />
          {labels[index]}
        </span>
      ))}
    </div>
  );
}

/** Shared tab-bar labels for the member-app demos. */
export const APP_TABS = {
  home: { en: 'Home', ar: 'الرئيسية' },
  book: { en: 'Book', ar: 'احجز' },
  bookings: { en: 'Bookings', ar: 'حجوزاتي' },
  profile: { en: 'Profile', ar: 'حسابي' },
} as const;

/**
 * Presence helper for step-driven elements: renders children with a shared
 * fade/slide when `show` is true. Keeps demo markup declarative.
 */
export function Reveal({
  show,
  y = 8,
  x = 0,
  delay = 0,
  className,
  children,
}: React.PropsWithChildren<{
  show: boolean;
  y?: number;
  x?: number;
  delay?: number;
  className?: string;
}>) {
  return (
    <motion.div
      className={className}
      initial={false}
      animate={{ opacity: show ? 1 : 0, y: show ? 0 : y, x: show ? 0 : x }}
      transition={{ duration: 0.5, ease: EASE_OUT_EXPO, delay: show ? delay : 0 }}
    >
      {children}
    </motion.div>
  );
}
